/**
 * Persistent browser cache for raster assets, keyed by s3Key (+ chroma variant).
 * Stores the decoded blob in IndexedDB and hands out a session-stable object URL,
 * so the same asset keeps the SAME `<img src>` across re-renders and reloads —
 * no reload flicker on app open or content refresh.
 */

const DB_NAME = "surf-asset-cache";
const DB_VERSION = 1;
const STORE = "assets";

/** cacheKey → object URL, stable for the page session (never re-created per render). */
const memUrls = new Map<string, string>();
/** cacheKey → in-flight producer promise (dedupe concurrent fetches). */
const inflight = new Map<string, Promise<string>>();

export function assetCacheKey(
  s3Key: string,
  chromaHex?: string,
  maxWidth?: number,
): string {
  let key = chromaHex ? `${s3Key}::chroma:${chromaHex.toLowerCase()}` : s3Key;
  if (maxWidth && maxWidth > 0) key += `::w${maxWidth}`;
  return key;
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<Blob | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result instanceof Blob ? req.result : null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbPut(key: string, blob: Blob): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(blob, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Resolve a stable object URL for an asset. Returns the cached URL if known;
 * otherwise reads the blob from IndexedDB, or produces it (fetch/transform),
 * persists it, and caches a session-stable object URL.
 */
export function getCachedAssetUrl(
  cacheKey: string,
  produceBlob: () => Promise<Blob>,
): Promise<string> {
  const existing = memUrls.get(cacheKey);
  if (existing) return Promise.resolve(existing);
  const pending = inflight.get(cacheKey);
  if (pending) return pending;

  const task = (async () => {
    let blob = await idbGet(cacheKey).catch(() => null);
    if (!blob) {
      blob = await produceBlob();
      void idbPut(cacheKey, blob).catch(() => undefined);
    }
    const url = URL.createObjectURL(blob);
    memUrls.set(cacheKey, url);
    return url;
  })();

  inflight.set(cacheKey, task);
  try {
    return task;
  } finally {
    void task.finally(() => inflight.delete(cacheKey));
  }
}

/** True when a stable URL is already available in memory (no async needed). */
export function peekCachedAssetUrl(cacheKey: string): string | undefined {
  return memUrls.get(cacheKey);
}
